<template>
  <bean-source-page
    type="index"
    :columns="tableColumns"
    :filter-columns="filterColumns"
    :actions="[]"
    :action-buttons="[]"
    :title="$t('menu.singleAchievementPayCompute')"
    :onFetchData="onFetchData"
    enableTableToExcel
  />
</template>

<script>
import { Vue, Component } from 'vue-property-decorator';
import { columnsExtend } from '../admin_grapes/trades.js';
import _ from 'lodash';
import moment from 'moment';

@Component
export default class SingleAchievementPayCompute extends Vue {
  get filterColumns() {
    return [
      {
        prop: 'account_id_eq',
        label: this.$t('model.account'),
        renderCell: {
          component: 'select',
          required: true,
          props: {
            clearable: true,
            xRemotePreload: async () => {
              const { data } = await this.$request.get('/accounts/read_options', { per_page: 100 });
              return data.map(item => ({ value: item.id, label: item.name }));
            },
            xRemoteSearch: async (keyword) => {
              const { data } = await this.$request.get('/accounts/read_options', { keyword });
              return data.map(item => ({ value: item.id, label: item.name }));
            }
          }
        }
      },
      {
        prop: 'redeem_share',
        label: '赎回份额',
        renderCell: {
          component: 'inputNumber',
          required: true,
          props: {
            type: 'number',
          }
        }
      },
      {
        prop: 'fund_id_eq',
        label: this.$t('model.fund'),
        renderCell: {
          component: 'select',
          required: true,
          props: {
            clearable: true,
            xRemotePreload: async () => {
              const { data } = await this.$request.get('/funds/read_options', { per_page: 100 });
              return data.map(item => ({ value: item.id, label: item.name }));
            },
            xRemoteSearch: async (keyword) => {
              const { data } = await this.$request.get('/funds/read_options', { keyword });
              return data.map(item => ({ value: item.id, label: item.name }));
            }
          }
        }
      },
      {
        prop: 'provision_date',
        label: '计提日期',
        renderCell: {
          component: 'datePicker',
          required: true,
          props: {
            valueFormat: 'yyyy-MM-dd'
          }
        }
      },
      {
        prop: 'provision_value',
        label: '计提净值',
        renderCell: {
          component: 'inputNumber',
          required: true,
          props: {
            type: 'number',
            step: 0.0001,
          }
        }
      },
      {
        prop: 'closed_period',
        label: '封闭期',
        renderCell: {
          component: 'select',
          required: true,
          props: {
            options: [{ label: '0', value: '0' }, { label: '365', value: '365' }]
          }
        }
      },
      {
        prop: 'water_line',
        label: '水位线',
        renderCell: {
          component: 'inputNumber',
          required: true,
          props: {
            type: 'number',
            step: 0.001,
            max: 1,
            min: 0,
          }
        }
      },
      {
        prop: 'provision_ratio',
        label: '计提比例',
        renderCell: {
          component: 'inputNumber',
          required: true,
          props: {
            type: 'number',
            step: 0.001,
            max: 1,
            min: 0,
          }
        }
      },
    ]
  }

  get tableColumns() {
    const array = [
      'account',
      {
        key: 'fund',
        width: 160,
      },
      'confirm_date',
      'type',
      {
        key: 'value',
        renderCell: 'netValue',
      },
      {
        key: 'share',
        renderCell: 'money',
        width: 120,
      },
      {
        key: 'amount',
        renderCell: 'money',
        width: 120,
      },
      {
        prop: 'last_provision_date',
        label: '上一次计提日期',
      },
      {
        prop: 'last_provision_value',
        label: '计提净值',
        renderCell: 'netValue',
      },
      {
        prop: 'rest_share',
        label: '剩余份额(先进先出)',
        renderCell: 'money',
        width: 120,
      },
      {
        prop: 'init_asset',
        label: '初始资产',
        renderCell: 'money',
        width: 120,
      },
      {
        prop: 'simulate_provision_date',
        label: '模拟计提日期',
      },
      {
        prop: 'simulate_provision_value',
        label: '模拟计提净值',
        renderCell: 'netValue',
      },
      {
        prop: 'close_period',
        label: '剩余封闭期',
      },
      {
        prop: 'water_line',
        label: '水位线',
        renderCell: 'percentage',
      },
      {
        prop: 'holding_days',
        label: '持有天数',
      },
      {
        prop: 'total_rate_of_return',
        label: '区间收益',
        renderCell: 'percentage',
      },
      {
        prop: 'rate_of_return',
        label: '是否计提',
        renderCell: 'percentage',
      },
      {
        prop: 'provision_ratio',
        label: '计提比例',
        renderCell: 'percentage',
      },
      {
        prop: 'simulate_provision_amount',
        label: '模拟计提金额',
        renderCell: 'money',
        width: 120,
      },
      {
        prop: 'simulate_provision_share',
        label: '模拟计提份额',
        renderCell: 'money',
        width: 120,
      },
      {
        prop: 'simulate_redeem_share',
        label: '模拟赎回份额',
        renderCell: 'money',
        width: 120,
      },
      {
        prop: 'simulate_redeem_provision_share',
        label: '模拟赎回应计提份额',
        renderCell: 'money',
        width: 120,
      },
      {
        prop: 'simulate_rest_share',
        label: '模拟剩余份额(先进先出)',
        renderCell: 'money',
        width: 120,
      },
      {
        prop: 'simulate_init_asset',
        label: '模拟初始资产',
        renderCell: 'money',
        width: 120,
      },
    ].filter(Boolean)

    return columnsExtend(array)
  }

  async onFetchData({ type }) {
    if (type === 'index' && _.get(this.$route.query, 'fund_id_eq')) {
      const data = await this.$request.get(
        '/trades/achievement_pay_compute', {
        params: {
          type_not_in: ['AchievementPayTrade', 'RedeemTrade'],
          state_eq: 'confirmed',
          ...this.$route.query,
          page: 0
        }
      });
      const closePeriod = _.get(this.$route.query, 'closed_period')
      let redeemShare = Number(_.get(this.$route.query, 'redeem_share'))
      const redeemableData = _.filter(data, datum => {
        const confirmDate = moment(datum.confirm_date)
        const endCloseDate = moment(confirmDate).add(closePeriod, 'days')
        return endCloseDate.isBefore(simulateProvisionDate)
      })
      if (_.sum(_.map(_.map(redeemableData, 'rest_share'), Number)) < redeemShare)  {
        this.$msgbox.alert('剩余可赎回份额不足, 请重新设置')
        return { data: [], pagination: {} }
      }

      const result = []
      const simulateProvisionDateQuery = _.get(this.$route.query, 'provision_date')
      const simulateProvisionDate = moment(simulateProvisionDateQuery)
      const simulateProvisionValue = _.get(this.$route.query, 'provision_value')
      const waterLine = _.get(this.$route.query, 'water_line')
      const provisionRatio = _.get(this.$route.query, 'provision_ratio')
      _.each(data, datum => {
        if (datum.rest_share) {
          result.push(datum)
          _.each(datum.children, child => {
            result.push(child)
          })

          const confirmDate = moment(datum.confirm_date)
          const endCloseDate = moment(confirmDate).add(closePeriod, 'days')
          if (endCloseDate.isAfter(simulateProvisionDate)) {
            datum.close_period = endCloseDate.diff(simulateProvisionDate, 'days')
            return
          }
          datum.close_period = 0
          let redeemRestShare = redeemShare
          if (redeemShare >= datum.rest_share) {
            redeemRestShare = datum.rest_share
            redeemShare = redeemShare - datum.rest_share
          } else {
            redeemShare = 0
          }

          const initValue = datum.last_provision_value || datum.value
          datum.init_asset = _.round(initValue * datum.rest_share, 2)
          datum.simulate_provision_date = simulateProvisionDateQuery
          datum.simulate_provision_value = simulateProvisionValue
          datum.water_line = waterLine
          const lastProvisionDate = moment(datum.last_provision_date || datum.confirm_date)
          const days = simulateProvisionDate.diff(lastProvisionDate, 'days')
          datum.holding_days = days
          datum.total_rate_of_return = (datum.simulate_provision_value - initValue) / initValue
          datum.rate_of_return = datum.total_rate_of_return / (days / 365)
          if (datum.rate_of_return > waterLine) {
            datum.provision_ratio = provisionRatio
            datum.simulate_provision_amount = (datum.rate_of_return - datum.water_line) * datum.init_asset * datum.provision_ratio * days / 365
            datum.simulate_provision_share = datum.simulate_provision_amount / datum.simulate_provision_value
            datum.simulate_redeem_provision_share = _.round(datum.simulate_provision_share * (redeemRestShare / datum.rest_share), 4)
            datum.simulate_redeem_share = _.round(redeemRestShare - datum.simulate_redeem_provision_share, 4)
            datum.simulate_rest_provision_share = _.round(datum.simulate_provision_share - datum.simulate_redeem_provision_share, 4)
          } else {
            datum.simulate_redeem_share = redeemRestShare
            datum.simulate_redeem_provision_share = 0
            datum.simulate_rest_provision_share = 0
          }
          datum.simulate_rest_share = +datum.rest_share - datum.simulate_redeem_share - datum.simulate_redeem_provision_share - datum.simulate_rest_provision_share
          datum.simulate_init_asset = datum.simulate_provision_value * datum.simulate_rest_share
        }
      })
      return {
        data: result,
        pagination: {}
      }
    } else {
      return {
        data: [],
        pagination: {}
      }
    }
  }
}

export const route = [
  {
    path: '/single_achievement_pay_compute',
    name: 'single_achievement_pay_compute',
    component: SingleAchievementPayCompute
  },
]
</script>
